import { Alert, Form, FormInstance, InputNumber, Select, Switch } from "antd";
import { DiffRow, fmtBool } from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { InlineField, SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { CutoffUnit } from "shared/types/enum";
import { fmtCutoff } from "shared/utils/cutoff";

const KEY = "cancellation";

type Values = {
  cancellation_policy_enabled: boolean;
  cancellation_cutoff_value?: number;
  cancellation_cutoff_unit?: CutoffUnit;
};

const unitLabel = (unit: CutoffUnit) =>
  translate(unit === CutoffUnit.Hour ? "class_settings.unit.hours" : "class_settings.unit.minutes");

const Fields = ({ form }: { form: FormInstance }) => {
  // The cutoff only shows while the rule is on.
  const enabled = Form.useWatch("cancellation_policy_enabled", form);
  const cutoffValue = Form.useWatch("cancellation_cutoff_value", form);
  // Hydrated to "hour" when the stored unit is null (policy off), so the
  // select never sits empty when the rule is switched on.
  const cutoffUnit: CutoffUnit = Form.useWatch("cancellation_cutoff_unit", form) ?? CutoffUnit.Hour;
  const toggleLabel = translate("class_settings.cancellation.toggle");
  const cutoffLabel = translate("class_settings.cancellation.cutoff");
  // Built at render time: translate() is not reactive at module scope.
  const unitOptions = [CutoffUnit.Hour, CutoffUnit.Minute].map((value) => ({
    value,
    label: unitLabel(value),
  }));

  return (
    <SettingsSection
      id={KEY}
      title={translate("class_settings.cancellation.section")}
      description={translate("settings_nav.desc.cancellation")}
    >
      {/* The scope note ("applies to client actions only") qualifies this
          one rule, so it is the switch row's hint. */}
      <SettingRow
        last
        title={toggleLabel}
        hint={translate("class_settings.cancellation.toggle_desc")}
        control={
          <Form.Item name="cancellation_policy_enabled" valuePropName="checked" noStyle>
            <Switch aria-label={toggleLabel} />
          </Form.Item>
        }
        below={
          enabled && (
            <>
              {/* The value is a noStyle item so its message is not printed
                  inside the line; the outer, nameless Form.Item prints it on
                  its own line under the row (the NotificationSettingsPage
                  pattern). The unit sits beside the number, never
                  Space.Compact (see ValidityStartPolicySection). */}
              <Form.Item style={{ margin: 0 }}>
                <InlineField label={cutoffLabel}>
                  <Form.Item
                    name="cancellation_cutoff_value"
                    noStyle
                    rules={[
                      {
                        required: true,
                        type: "integer",
                        min: 1,
                        message: translate(
                          cutoffUnit === CutoffUnit.Hour
                            ? "class_settings.cancellation.min_error_hour"
                            : "class_settings.cancellation.min_error_minute",
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      style={{ width: 90 }}
                      aria-label={cutoffLabel}
                    />
                  </Form.Item>
                  <Form.Item name="cancellation_cutoff_unit" noStyle>
                    <Select
                      aria-label={translate("label.unit")}
                      options={unitOptions}
                      style={{ width: 110 }}
                    />
                  </Form.Item>
                </InlineField>
              </Form.Item>
              {typeof cutoffValue === "number" && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 14 }}
                  message={translate("class_settings.cancellation.consequence").replace(
                    "{cutoff}",
                    fmtCutoff(cutoffValue, cutoffUnit),
                  )}
                />
              )}
            </>
          )
        }
      />
    </SettingsSection>
  );
};

export const cancellationPolicy: PolicySection = {
  key: KEY,
  initialValues: (s) => ({
    cancellation_policy_enabled: s.cancellation_policy_enabled,
    cancellation_cutoff_value: s.cancellation_cutoff_value ?? undefined,
    // Null while the policy is off: hydrate the select to hours so enabling
    // the rule sends a unit with the value.
    cancellation_cutoff_unit: s.cancellation_cutoff_unit ?? CutoffUnit.Hour,
  }),
  buildSave: (v: Values, s) => {
    const isEnabled = !!v.cancellation_policy_enabled;
    const nextValue: number | null = isEnabled ? Number(v.cancellation_cutoff_value) : null;
    const nextUnit: CutoffUnit | null = isEnabled
      ? v.cancellation_cutoff_unit ?? s.cancellation_cutoff_unit ?? CutoffUnit.Hour
      : null;

    const rows: DiffRow[] = [];
    if (isEnabled !== s.cancellation_policy_enabled) {
      rows.push({
        label: translate("class_settings.cancellation.toggle"),
        before: fmtBool(s.cancellation_policy_enabled),
        after: fmtBool(isEnabled),
      });
    }
    // One row whether the number, the unit or both changed; both halves are
    // null while off, so switching off lists "12 hours → —" once.
    if (nextValue !== s.cancellation_cutoff_value || nextUnit !== s.cancellation_cutoff_unit) {
      rows.push({
        label: translate("class_settings.cancellation.cutoff"),
        before: fmtCutoff(s.cancellation_cutoff_value, s.cancellation_cutoff_unit),
        after: fmtCutoff(nextValue, nextUnit),
      });
    }

    return {
      rows,
      payload: {
        cancellation_policy_enabled: isEnabled,
        ...(isEnabled
          ? { cancellation_cutoff_value: nextValue!, cancellation_cutoff_unit: nextUnit! }
          : {}),
      },
      note: translate("class_settings.confirm.note_cancellation"),
    };
  },
  Fields,
};
